
import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAllUserPetPosts } from '../hooks/usePosts';
import { useUserPets } from '../hooks/useUserPets';
import { useFollow } from '../hooks/useFollow';
import { PawPrint, Calendar, MapPin, Mail, Camera, Edit2, Save, X, Navigation } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Label } from '../components/ui/label';
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';
import { optimizeImage } from '../utils/imageOptimization';
import { useGeolocation } from '../hooks/useGeolocation';
import { useLocationValidation } from '../hooks/useLocationValidation';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';

const Profile = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Get posts and pets for the current user
  const { data: allPetPosts, isLoading: postsLoading } = useAllUserPetPosts(user?.id);
  const { data: pets, isLoading: petsLoading } = useUserPets(user?.id);
  const { followerCount, followingCount } = useFollow(user?.id);
  
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.user_metadata?.full_name || '',
    bio: user?.user_metadata?.bio || '',
    location: user?.user_metadata?.location || '', // Keep for backward compatibility
    address: user?.user_metadata?.address || '',
    city: user?.user_metadata?.city || '',
    state: user?.user_metadata?.state || '',
    country: user?.user_metadata?.country || '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { detectLocation, isDetecting, error: geoError } = useGeolocation();
  const { geocodeAddress } = useLocationValidation();

  const isOwnProfile = !userId || userId === user?.id;

  // If not own profile, redirect to user profile page
  if (!isOwnProfile) {
    navigate(`/user/${userId}`);
    return null;
  }

  const handleEdit = () => {
    setIsEditing(true);
    setFormData({
      full_name: user?.user_metadata?.full_name || '',
      bio: user?.user_metadata?.bio || '',
      location: user?.user_metadata?.location || '',
      address: user?.user_metadata?.address || '',
      city: user?.user_metadata?.city || '',
      state: user?.user_metadata?.state || '',
      country: user?.user_metadata?.country || '',
      latitude: undefined,
      longitude: undefined
    });
  };

  const handleSave = async () => {
    try {
      // ✅ VALIDACIÓN CRÍTICA: Si se ingresó una ubicación, DEBE tener coordenadas
      if (formData.address && (!formData.latitude || !formData.longitude)) {
        toast.error('Please select your location from the suggestions to ensure accurate distance calculations for pet matching.');
        return;
      }

      // Update auth user metadata (mantener para compatibilidad)
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          full_name: formData.full_name,
          bio: formData.bio,
          location: formData.location,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country
        }
      });

      if (authError) throw authError;

      // Update profiles table with all geographic fields
      const { error: dbError } = await supabase
        .from('profiles')
        .update({
          display_name: formData.full_name,
          bio: formData.bio,
          location: formData.location, // Keep for backward compatibility
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          latitude: formData.latitude,
          longitude: formData.longitude,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user?.id);

      if (dbError) throw dbError;

      toast.success(t('profile.profileUpdated'));
      setIsEditing(false);
      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('profile.profileUpdateFailed'));
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      full_name: user?.user_metadata?.full_name || '',
      bio: user?.user_metadata?.bio || '',
      location: user?.user_metadata?.location || '',
      address: user?.user_metadata?.address || '',
      city: user?.user_metadata?.city || '',
      state: user?.user_metadata?.state || '',
      country: user?.user_metadata?.country || '',
      latitude: undefined,
      longitude: undefined
    });
  };

  const handleDetectLocation = async () => {
    const detectedLocation = await detectLocation();
    if (detectedLocation) {
      try {
        // Reverse geocode to get address components
        const { data, error } = await supabase.functions.invoke('validate-location', {
          body: {
            type: 'reverse_geocode',
            latitude: detectedLocation.latitude,
            longitude: detectedLocation.longitude
          }
        });

        if (error) throw error;

        if (data && data.address_components) {
          const addr = data.address_components;
          const fullAddress = data.formatted_address || detectedLocation.location;
          const cityName = addr.city || addr.locality || '';
          const stateName = addr.state || '';
          const countryName = addr.country || '';
          
          setFormData(prev => ({
            ...prev,
            address: fullAddress,
            city: cityName,
            state: stateName,
            country: countryName,
            latitude: detectedLocation.latitude,
            longitude: detectedLocation.longitude,
            location: `${cityName}, ${stateName}, ${countryName}` // Backward compatibility
          }));
          toast.success("Location detected and address updated");
        } else {
          // Fallback: just coordinates
          setFormData(prev => ({ 
            ...prev, 
            location: detectedLocation.location,
            latitude: detectedLocation.latitude,
            longitude: detectedLocation.longitude
          }));
          toast.success("Location coordinates updated");
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
        // Fallback: just coordinates
        setFormData(prev => ({ 
          ...prev, 
          location: detectedLocation.location,
          latitude: detectedLocation.latitude,
          longitude: detectedLocation.longitude
        }));
        toast.success("Location coordinates updated");
      }
    } else if (geoError) {
      toast.error(geoError);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawFile = event.target.files?.[0];
    if (!rawFile || !user) return;

    try {
      setUploading(true);

      // Optimize the avatar before upload (smaller, faster, WebP when available).
      const file = rawFile.type.startsWith('image/')
        ? await optimizeImage(rawFile, { preset: 'avatar' })
        : rawFile;

      // Create a unique file name with user ID
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Upload the file to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: publicURL } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      if (publicURL) {
        // Update both auth user metadata AND profiles table
        const avatarUrl = publicURL.publicUrl;
        
        // Update auth user metadata (mantener para compatibilidad)
        const { error: authUpdateError } = await supabase.auth.updateUser({
          data: { avatar_url: avatarUrl }
        });

        if (authUpdateError) throw authUpdateError;

        // Update profiles table (✅ FASE 2: Migrado de users → profiles)
        const { error: dbUpdateError } = await supabase
          .from('profiles')
          .update({ 
            avatar_url: avatarUrl,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);

        if (dbUpdateError) throw dbUpdateError;

        toast.success(t('profile.photoUpdated'));
        // Refresh the page to show the new avatar
        window.location.reload();
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(t('profile.photoUploadError'));
    } finally {
      setUploading(false);
    }
  };

  const getUserInitials = () => {
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name
        .split(' ')
        .map((name: string) => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const joinDate = user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="space-y-6">
        {/* Profile Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="relative flex-shrink-0 mx-auto md:mx-0">
                <Avatar className="w-24 h-24" key={user?.user_metadata?.avatar_url || user?.id}>
                  <AvatarImage 
                    src={user?.user_metadata?.avatar_url} 
                    alt={user?.user_metadata?.full_name || 'User'}
                    loading="eager"
                  />
                  <AvatarFallback className="w-24 h-24 bg-pet-purple text-white text-2xl font-bold">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                
                {isOwnProfile && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50"
                    disabled={uploading}
                  >
                    <Camera className="h-4 w-4 text-gray-600" />
                  </button>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              
              <div className="flex-1 space-y-4 w-full text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="space-y-2 flex-1">
                    {isEditing ? (
                      <Input
                        value={formData.full_name}
                        onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                        placeholder={t('profile.fullName')}
                        className="text-xl md:text-2xl font-bold text-center md:text-left"
                      />
                    ) : (
                      <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                        {user?.user_metadata?.full_name || user?.email || 'User'}
                      </h1>
                    )}
                    
                    {isEditing ? (
                      <Textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({...formData, bio: e.target.value})}
                        placeholder={t('profile.bioPlaceholder')}
                        className="min-h-[60px]"
                      />
                    ) : (
                      <p className="text-gray-600">
                        {user?.user_metadata?.bio || t('profile.noBio')}
                      </p>
                    )}
                  </div>
                  
                  {isOwnProfile && (
                    <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0 w-full sm:w-auto">
                      {isEditing ? (
                        <>
                          <Button onClick={handleSave} size="sm" className="w-full sm:w-auto">
                            <Save className="h-4 w-4 mr-1" />
                            {t('profile.save')}
                          </Button>
                          <Button onClick={handleCancel} variant="outline" size="sm" className="w-full sm:w-auto">
                            <X className="h-4 w-4 mr-1" />
                            {t('profile.cancel')}
                          </Button>
                        </>
                      ) : (
                        <Button onClick={handleEdit} variant="outline" size="sm" className="w-full sm:w-auto">
                          <Edit2 className="h-4 w-4 mr-1" />
                          {t('profile.editProfile')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {/* User info section - organized vertically for better layout */}
                <div className="space-y-3 text-sm text-gray-500">
                  <div className="flex items-center justify-center md:justify-start gap-1">
                    <Calendar className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{t('profile.joined')} {joinDate}</span>
                  </div>
                  
                  {/* Location section - Structured address fields */}
                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="address" className="text-sm font-medium text-muted-foreground">
                          Full Address <span className="text-destructive">*</span>
                          <span className="text-xs text-muted-foreground block mt-1">
                            Required for accurate pet matching distances
                          </span>
                        </Label>
                        <LocationAutocomplete
                          value={formData.address}
                          onChange={async (value, placeId) => {
                            console.log('Address changed:', { value, placeId });
                            setFormData({...formData, address: value});
                            
                            // Auto-geocode when address is selected
                            if (placeId) {
                              try {
                                const geocoded = await geocodeAddress(placeId);
                                if (geocoded) {
                                  const addr = geocoded.address_components;
                                  setFormData(prev => ({
                                    ...prev,
                                    address: geocoded.formatted_address,
                                    city: addr?.locality || addr?.city || '',
                                    state: addr?.administrative_area_level_1 || addr?.state || '',
                                    country: addr?.country || '',
                                    latitude: geocoded.latitude,
                                    longitude: geocoded.longitude,
                                    location: `${addr?.locality || addr?.city}, ${addr?.administrative_area_level_1 || addr?.state}, ${addr?.country}`
                                  }));
                                  toast.success('Address details populated with coordinates ✓');
                                }
                              } catch (error) {
                                console.error('Geocoding error:', error);
                                toast.error('Could not get coordinates. Please select from suggestions.');
                              }
                            }
                          }}
                          placeholder="Enter your full address (required for matching)"
                          type="address"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="city" className="text-sm font-medium text-muted-foreground">
                            City
                          </Label>
                          <Input
                            id="city"
                            value={formData.city}
                            onChange={(e) => setFormData({...formData, city: e.target.value})}
                            placeholder="City"
                            className="bg-background"
                          />
                        </div>
                        <div>
                          <Label htmlFor="state" className="text-sm font-medium text-muted-foreground">
                            State/Province
                          </Label>
                          <Input
                            id="state"
                            value={formData.state}
                            onChange={(e) => setFormData({...formData, state: e.target.value})}
                            placeholder="State"
                            className="bg-background"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="country" className="text-sm font-medium text-muted-foreground">
                          Country
                        </Label>
                        <Input
                          id="country"
                          value={formData.country}
                          onChange={(e) => setFormData({...formData, country: e.target.value})}
                          placeholder="Country"
                          className="bg-background"
                        />
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleDetectLocation}
                        disabled={isDetecting}
                        className="w-full"
                        title="Auto-detect location using GPS"
                      >
                        <Navigation className="h-4 w-4 mr-2" />
                        {isDetecting ? t('profile.detecting') : 'Detect Current Location (GPS)'}
                      </Button>
                      {geoError && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <span>⚠️</span>
                          <span>{geoError} - Please enter your location manually</span>
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        💡 Click GPS to auto-detect or type your location manually. Location is required for pet matching.
                      </p>
                    </div>
                  ) : (
                    user?.user_metadata?.location && (
                      <div className="flex items-center justify-center md:justify-start gap-1">
                        <MapPin className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{user.user_metadata.location}</span>
                      </div>
                    )
                  )}
                  
                  {/* Email section - separate line for better organization */}
                  {isOwnProfile && (
                    <div className="flex items-center justify-center md:justify-start gap-1">
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{user?.email}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex flex-wrap justify-center md:justify-start gap-4 sm:gap-6 text-sm">
                  <div className="text-center">
                    <div className="font-bold text-lg">{postsLoading ? '...' : allPetPosts?.length || 0}</div>
                    <div className="text-gray-500">{t('profile.posts')}</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg">{petsLoading ? '...' : pets?.length || 0}</div>
                    <div className="text-gray-500">{t('profile.pets')}</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg">{followerCount || 0}</div>
                    <div className="text-gray-500">{t('profile.followers')}</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg">{followingCount || 0}</div>
                    <div className="text-gray-500">{t('profile.following')}</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.recentPosts')}</CardTitle>
            </CardHeader>
            <CardContent>
              {postsLoading ? (
                <p className="text-gray-500">{t('profile.loadingPosts')}</p>
              ) : allPetPosts && allPetPosts.length > 0 ? (
                <div className="space-y-3">
                  {allPetPosts.slice(0, 3).map((post) => (
                    <div key={post.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                      {post.media && post.media.length > 0 ? (
                        post.media[0].media_type && post.media[0].media_type.includes('video') ? (
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center">
                            <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        ) : (
                          <img 
                            src={post.media[0].url} 
                            alt={post.caption || "Post"}
                            className="w-12 h-12 rounded object-cover"
                          />
                        )
                      ) : (
                        <div className="w-12 h-12 bg-pet-purple/20 rounded flex items-center justify-center">
                          <PawPrint className="h-6 w-6 text-pet-purple" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {post.caption || t('profile.noCaption')}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          by {post.pet?.name || t('profile.unknownPet')}
                        </p>
                      </div>
                    </div>
                  ))}
                  {allPetPosts.length > 3 && (
                    <p className="text-sm text-gray-500 text-center">
                      {t('profile.andMore', { count: allPetPosts.length - 3 })}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">{t('profile.noPosts')}</p>
              )}
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.pets')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {petsLoading ? (
                  <p className="text-gray-500">{t('profile.loadingPets')}</p>
                ) : pets && pets.length > 0 ? (
                  <div className="space-y-3">
                    {pets.slice(0, 3).map((pet) => (
                      <div key={pet.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                        {pet.photo_url ? (
                          <img 
                            src={pet.photo_url} 
                            alt={pet.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-pet-purple/20 rounded-full flex items-center justify-center">
                            <PawPrint className="h-6 w-6 text-pet-purple" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{pet.name}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {translateSpeciesSingular(pet.species, t)} {pet.breed ? `• ${pet.breed}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                    {pets.length > 3 && (
                      <p className="text-sm text-gray-500 text-center">
                        {t('profile.andMore', { count: pets.length - 3 })}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">{t('myPets.noPetsYet')}</p>
                )}
                <Button 
                  onClick={() => navigate('/my-pets')}
                  className="w-full"
                >
                  {pets && pets.length > 0 ? t('profile.viewAllPets') : t('profile.addFirstPet')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
